import { useState, useEffect, useRef } from "react";

export interface ResponseState<T> {
  data: T | null;
  isLoading: boolean;
  error: any;
}

function useAbortableFetch<T = any>(url: string, options?: RequestInit): ResponseState<T> {
  const [response, setResponse] = useState<ResponseState<T>>({
    data: null,
    isLoading: true,
    error: null,
  });

  const abortController = useRef(new AbortController());

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(url, { ...options, signal: abortController.current.signal });

        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }

        const data: T = await res.json();

        if (!abortController.current.signal.aborted) {
          setResponse({
            data,
            isLoading: false,
            error: null,
          });
        }
      } catch (error) {
        if (!abortController.current.signal.aborted) {
          setResponse({
            data: null,
            isLoading: false,
            error: (<Error>error).message,
          });
        }
      }
    };

    fetchData();

    return () => {
      abortController.current.abort();
    };
  }, [url, options]);

  return response;
}

export { useAbortableFetch };
